/* eslint-disable react/no-did-update-set-state */
import React, { Component } from 'react'
import { Route, Redirect } from 'react-router'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'

import i18n from '../../services/i18n';
import * as AppActions from '../../actions/app';
import * as CountriesActions from '../../actions/countries';
import * as ChainsActions from '../../actions/chains';
import * as NotificationsActions from '../../actions/notifications';
import * as UserActions from '../../actions/user';
import * as ConfigActions from '../../actions/config';
import * as MediaActions from '../../actions/content/media';
import { Routes } from '../../constants';
import { Iframe } from '../../components';
import Layout from '../Layout';
import SelectConfirm from '../../components/SelectConfirm';
import { getSelectedEstate } from '../../reducers/app';
import { history } from '../../store';
import { AppUtils, EstateUtils } from '../../utils';
import ErrorPage from '../ErrorPage';
import {
  getDefaultEstate,
  getUrlEstate,
  shouldRenderWithIframe,
  isValidEstateUrl,
  urlNeedsFormatting,
} from './helpers';
import config from '../../config';

class PrivateRoute extends Component {
  static propTypes = {
    acls: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          acl: PropTypes.string.isRequired,
          ignoreAdmin: PropTypes.bool,
        }),
      ]),
    ),
    app: PropTypes.shape({}).isRequired,
    component: PropTypes.func.isRequired,
    currentEstate: PropTypes.shape({}).isRequired,
    dismiss: PropTypes.func.isRequired,
    dismissAll: PropTypes.func.isRequired,
    dismissFlash: PropTypes.func.isRequired,
    env: PropTypes.string.isRequired,
    estates: PropTypes.arrayOf(PropTypes.object).isRequired,
    fetchAllMediaCounts: PropTypes.func.isRequired,
    fetchChains: PropTypes.func.isRequired,
    fetchConfigs: PropTypes.func.isRequired,
    fetchCountries: PropTypes.func.isRequired,
    fetchOwnProfile: PropTypes.func.isRequired,
    flashMessages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    isProfileFetched: PropTypes.bool.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
      state: PropTypes.shape({
        from: PropTypes.string,
      }),
    }).isRequired,
    logout: PropTypes.func.isRequired,
    messages: PropTypes.arrayOf(PropTypes.object).isRequired,
    noEstate: PropTypes.bool,
    pathname: PropTypes.string.isRequired,
    redirect: PropTypes.string,
    roles: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          role: PropTypes.string.isRequired,
          ignoreAdmin: PropTypes.bool,
        }),
      ]),
    ),
    selectedEstate: PropTypes.string.isRequired,
    selectEstate: PropTypes.func.isRequired,
    sessionId: PropTypes.string.isRequired,
    t: PropTypes.func.isRequired,
    username: PropTypes.string,
  };

    static defaultProps = {
        acls: [],
        noEstate: false,
        redirect: Routes.buildRoute(Routes.LOGIN),
        roles: [],
        username: null,
    }

    constructor(...props) {
        super(...props)

        this.state = {
            error: null,
            isLanguageSelectorOpen: false,
            languages: [],
        }

        this.intervalCounts = null
    }

    /**
     * Before initial render
     */
    componentDidMount() {
        const { env: currentEnv, estates, fetchAllMediaCounts, isAuthenticated, logout, noEstate, selectedEstate, selectEstate } = this.props

        // If environment has changed, user needs to log in again
        if (process.env.NODE_ENV !== currentEnv) {
            return logout()
        }

        if (noEstate) {
            return
        }

        // If estate is set in the url, select it.
        if (isValidEstateUrl(this.props)) {
            const urlEstate = getUrlEstate(this.props)

            // If the estate is different from the selected
            if (urlEstate !== selectedEstate) {
                selectEstate(urlEstate)
            }
        } else {
            // If url doesn't have a valid estate, select the default one as selected estate
            const estate = getDefaultEstate(estates, selectedEstate)

            // If estate hasn't been already selected
            if (estate !== selectedEstate) {
                selectEstate(estate)
            }

            // If the estate param hasn't been formatted, change current url with the right one
            if (urlNeedsFormatting(this.props)) {
                this.formatUrlAndRedirect()
            }
        }

        // If authenticated and has permission to at least one estate, fetch global data
        if (isAuthenticated && estates.length > 0) {
            this.refreshData()

            // Fetch counts
            this.intervalCounts = setInterval(() => fetchAllMediaCounts(), config.statistics.resfreshTime)
            fetchAllMediaCounts()
        }
    }

    /**
     * Before every rendering when updating
     * @param {Object} prevProps
     */
    componentDidUpdate(prevProps) {
        const { acls: lastAcls, roles: lastRoles } = prevProps
        const { acls, app, estates, isAuthenticated, noEstate, selectedEstate, selectEstate } = this.props
        const lastEstate = getUrlEstate(prevProps)
        const newEstate = getUrlEstate(this.props) || getDefaultEstate(estates, selectedEstate)
        const roles = this.getRoles()
        // This roles must be included if any, not anought being admin
        const mandatoryAcls = this.getMandatoryACl()
        const mandatoryRoles = this.getMandatoryRoles()

        // First of all, check if a redirection is needed because of a wrong url
        if (urlNeedsFormatting(this.props)) {
            return this.formatUrlAndRedirect(lastEstate === newEstate || !isValidEstateUrl(this.props))
        }

        // State has changed and it is a valid estate
        if (
            lastEstate !== newEstate && // Estate in url changes
            newEstate !== selectedEstate // Is not already selected in redux
            // isValidEstateUrl(prevProps) // Is a valid estate
        ) {
            selectEstate(newEstate)

            // Fetch global data if authenticated and with estates
            if (isAuthenticated && estates.length > 0) {
                this.refreshData()
            }
        }

        const { error } = this.state

        if (!noEstate) {
            const hasSomeAcl = !!acls.length // Page has some acl
            const hasSomeRole = !!roles.length // Page has some role
            const haveAclsChanged = lastAcls !== acls // Acl list has changed
            const haveRolesChanged = lastRoles !== roles // Role list has changed
            const hasPermission =
                EstateUtils.hasPermission({ app }, { acls, roles }) && // Has admin or permission
                EstateUtils.hasPermission({ app }, { acls: mandatoryAcls, roles: mandatoryRoles }, { ignoreAdmin: true }) // Has permission no matter if admin

            if (
                (hasSomeAcl || hasSomeRole) &&
                (haveAclsChanged || haveRolesChanged) &&
                !hasPermission &&
                !error // This check stops infinite re-rendering when user doesn't have permission to the route
            ) {
                this.setState({
                    error: {
                        message: 'Permissions error',
                        stack: 'You have not enought permissions to access this page',
                    },
                })
                return
            }
        }

        const {
            location: { pathname },
        } = this.props
        const {
            location: { pathname: lastPathname },
        } = prevProps

        if (pathname !== lastPathname && error) {
            this.setState({ error: null })
        }
    }

    // Something failed
    componentDidCatch(error, info) {
        console.error(error, info)
        this.setState({
            error: {
                message: error.message,
                componentStack: info.componentStack,
                stack: error.stack,
            },
        })
    }

    componentWillUnmount() {
        if (this.intervalCounts) {
            clearInterval(this.intervalCounts)
        }
    }

    /**
     * Formats current pathname with default estate and redirects
     * @param {Boolean} [omitEstateSelection]
     */
    formatUrlAndRedirect = (omitEstateSelection = false) => {
        const { estates, pathname, selectedEstate, selectEstate } = this.props
        const estate = getDefaultEstate(estates, selectedEstate)

        if (!omitEstateSelection || estate !== selectedEstate) {
            selectEstate(estate)
        }

        history.replace({
            pathname: Routes.buildRoute(pathname, { estate }),
        })
    }

    /**
     * Gets a plain string role array
     */
    getRoles = () => {
        const { roles } = this.props
        return roles.map((item) => {
            if (typeof item === 'object') {
                return item.role
            }

            return item
        })
    }

    /**
     * Gets a plain string role array that doesn't allow being admin
     */
    getMandatoryACl = () => {
        const { acls } = this.props
        return acls
            .filter((item) => {
                if (typeof item === 'object') {
                    return item.ignoreAdmin
                }

                return false
            })
            .map((item) => {
                if (typeof item === 'object') {
                    return item.acl
                }

                return item
            })
    }

    /**
     * Gets a plain string role array that doesn't allow being admin
     */
    getMandatoryRoles = () => {
        const { roles } = this.props
        return roles
            .filter((item) => {
                if (typeof item === 'object') {
                    return item.ignoreAdmin
                }

                return false
            })
            .map((item) => {
                if (typeof item === 'object') {
                    return item.role
                }

                return item
            })
    }

    onClickLogout() {
        const { logout } = this.props
        // eslint-disable-next-line no-alert,no-restricted-globals
        if (confirm('Do you really want to log out?')) {
            logout()
        }
    }

    /**
     * When another estate is selected
     * @param {String} estateId Estate ID
     */
    onChangeEstate = (estateId) => {
        const { pathname, selectedEstate } = this.props

        if (selectedEstate !== estateId) {
            let newPathname = pathname.replace(selectedEstate, estateId)

            if (newPathname === '/') {
                newPathname = `/${estateId}`
            }

            history.replace(newPathname)
        }
    }

    formatLanguageArray = (languages, data) => {
        const formatedLanguages = []
        languages.forEach((language) => {
            formatedLanguages.push({
                value: language,
                name: data[language].name,
            })
        })
        return formatedLanguages
    }

    onClickOpenChangeLanguage = () => {
        const {
            store: { data },
        } = i18n
        let languages = Object.keys(data)
        languages = this.formatLanguageArray(languages, data)
        this.setState({
            isLanguageSelectorOpen: true,
            languages,
        })
    }

    onClickConfigureDevice = () => {
        history.push(Routes.buildRoute(Routes.CONFIGURE))
    }

    onClickNavigateLog = () => {
        history.push(Routes.buildRoute(Routes.LOGS))
    }

    onClickNavigateReports = () => {
        history.push(Routes.buildRoute(Routes.REPORTS))
    }

    onClickNavigateOperationsHub = () => {
        history.push(Routes.buildRoute(Routes.OPERATIONS_HUB))
    }

    onAcceptLanguageSelection = (selectedLanguage) => {
        i18n.changeLanguage(selectedLanguage, () => localStorage.setItem('hubble2Language', selectedLanguage))
        this.setState({ isLanguageSelectorOpen: false })
    }

    onCancelLanguageSelection = () => {
        this.setState({ isLanguageSelectorOpen: false })
    }

  refreshData() {
    const {
      isProfileFetched,
      fetchChains,
      fetchCountries,
      fetchConfigs,
      fetchOwnProfile,
    } = this.props;

        if (shouldRenderWithIframe(this.props) || !isValidEstateUrl(this.props)) {
            return null
        }

    fetchChains();
    fetchCountries();
    fetchConfigs();

        if (!isProfileFetched) {
            fetchOwnProfile()
        }
    }

    goBackFromError = () => {
        history.goBack()
        this.setState({ error: null })
    }

    renderInnerContent(moreProps) {
        const { component: Comp, currentEstate } = this.props
        const { error } = this.state
        const { urlWebsite } = currentEstate || {}

        const withIframe = shouldRenderWithIframe(this.props)

        if (error && typeof error === 'object') {
            const { message, stack, componentStack } = error
            return (
                <ErrorPage
                    errorHeader="Error"
                    errorContent="Do you want to send us the error information?"
                    error={message}
                    errorInfo={stack}
                    infoStack={componentStack}
                    goBack={this.goBackFromError}
                />
            )
        }

        if (withIframe) {
            return <Iframe src={urlWebsite} />
        }

        return <Comp {...moreProps} />
    }

    render() {
        if (urlNeedsFormatting(this.props)) {
            return 'REDIRECTING...'
        }

        const {
            estates,
            flashMessages,
            isAuthenticated,
            component: Comp,
            redirect,
            location,
            location: { pathname: locationPathname, search },
            logout,
            messages,
            dismiss,
            dismissAll,
            dismissFlash,
            pathname,
            selectedEstate,
            username,
            t,
            ...rest
        } = this.props

        if (!isAuthenticated) {
            return (
                <Route
                    {...rest}
                    render={() => (
                        <Redirect
                            to={{
                                pathname: redirect,
                                state: { from: locationPathname + search },
                            }}
                        />
                    )}
                />
            )
        }

        const { isLanguageSelectorOpen, languages } = this.state
        const withIframe = shouldRenderWithIframe(this.props)

        if (estates.length === 0) {
            return <ErrorPage errorHeader="Error" errorContent={t('NoEstateAssigned')} location={location} logoutButton />
        }

        return (
            <Route
                {...rest}
                render={(props) => (
                    <Layout
                        estates={estates}
                        flashMessages={flashMessages}
                        fullscreen={withIframe}
                        history={history}
                        isIframe={AppUtils.isIframe(search)}
                        messages={messages}
                        onClickChangeLanguage={() => this.onClickOpenChangeLanguage()}
                        onClickConfigureDevice={() => this.onClickConfigureDevice()}
                        onClickEstate={this.onChangeEstate}
                        onClickLogout={() => this.onClickLogout()}
                        onClickNavigateLog={this.onClickNavigateLog}
                        onClickNavigateOperationsHub={this.onClickNavigateOperationsHub}
                        onClickNavigateReports={this.onClickNavigateReports}
                        onDismiss={dismiss}
                        onDismissAll={dismissAll}
                        onDismissFlash={dismissFlash}
                        pathname={pathname}
                        selectedEstate={selectedEstate}
                        username={username}
                    >
                        <SelectConfirm
                            header={t('Select a language')}
                            isOpen={isLanguageSelectorOpen}
                            list={languages}
                            onClickAccept={this.onAcceptLanguageSelection}
                            onClickCancel={this.onCancelLanguageSelection}
                            selected={i18n.language}
                        />
                        {this.renderInnerContent(props)}
                    </Layout>
                )}
            />
        )
    }
}

const mapStateToProps = (state) => {
    const {
        app,
        app: { estates, selectedEstate },
        notifications: { flashMessages, messages },
        router: {
            location: { pathname },
        },
        user: {
            session: { env: currentEnv, email, isAuthenticated, sessionId },
            profile: { isFetched: isProfileFetched },
        },
    } = state

    return {
        app,
        currentEstate: getSelectedEstate(state) || {},
        env: currentEnv,
        estates,
        isAuthenticated,
        isProfileFetched,
        flashMessages,
        messages,
        pathname,
        selectedEstate,
        sessionId,
        username: email,
    }
}

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      ...AppActions,
      ...CountriesActions,
      ...ChainsActions,
      ...NotificationsActions,
      ...UserActions,
      ...ConfigActions,
      logout: UserActions.logout,
      fetchAllMediaCounts: MediaActions.fetchAllMediaCounts,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(PrivateRoute))
